import { useEffect, useState, type FormEvent } from 'react';
import {
  getAdminKeyFile,
  rotateAdminPassword,
  type AdminKey,
} from '../api/client';

/**
 * Admin service-mode key (FR-013а). The operator downloads `mycopy-admin.key`
 * (a JSON `{keyId, passwordHash}`) onto a USB stick; a technician inserts it at
 * a kiosk to open service mode and types the password set here. Rotating the
 * password bumps the keyId and revokes every previously-downloaded key file.
 */
export function AdminKeyPage() {
  const [key, setKey] = useState<AdminKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [noKey, setNoKey] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setNoKey(false);
    try {
      setKey(await getAdminKeyFile());
    } catch {
      // 404 = no key set yet (must rotate to create one).
      setKey(null);
      setNoKey(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function handleDownload() {
    if (!key) return;
    const content = JSON.stringify(
      { keyId: key.keyId, passwordHash: key.passwordHash },
      null,
      2,
    );
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mycopy-admin.key';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleRotate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await rotateAdminPassword(password);
      setPassword('');
      setConfirm('');
      setSuccess(
        `Password set. New key id ${res.keyId}. Re-download mycopy-admin.key — old key files are now revoked.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="mb-6">Admin key</h2>

      <div className="card mb-6" style={{ maxWidth: 640 }}>
        <div className="card-header">Service-mode USB key (mycopy-admin.key)</div>
        <div className="card-body">
          <p className="text-secondary" style={{ fontSize: '0.88rem', marginBottom: 16 }}>
            Download this file onto a USB stick. A technician inserts the stick at
            a kiosk to open service mode, then types the admin password set below.
            The password hash never leaves the server in usable form — the file
            only carries the rotation id and a bcrypt hash.
          </p>

          {loading ? (
            <div className="text-secondary">Loading…</div>
          ) : noKey ? (
            <div style={{ padding: '10px 14px', background: '#fef9c3', color: '#854d0e', borderRadius: 4, fontSize: '0.88rem' }}>
              No admin key yet. Set a password below to create one.
            </div>
          ) : (
            <table className="info-table" style={{ marginBottom: 16 }}>
              <tbody>
                <tr>
                  <td className="text-secondary" style={{ width: '40%' }}>Current key id</td>
                  <td><code>{key?.keyId}</code></td>
                </tr>
              </tbody>
            </table>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || noKey || !key}
          >
            Download mycopy-admin.key
          </button>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 640 }}>
        <div className="card-header">Set / rotate admin password</div>
        <div className="card-body">
          <p className="text-secondary" style={{ fontSize: '0.88rem', marginBottom: 16 }}>
            Setting a new password revokes every previously-downloaded key file.
            After rotating, re-download and re-distribute the USB key.
          </p>
          <form onSubmit={handleRotate}>
            <div className="form-group">
              <label>New admin password (min 6 chars)</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                minLength={6}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="form-group">
              <label>Confirm password</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                minLength={6}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            {success && (
              <div style={{ padding: '10px 14px', background: '#dcfce7', color: '#166534', borderRadius: 4, fontSize: '0.88rem', marginBottom: 16 }}>
                {success}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Set password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
